import { apiGet, apiPost, apiPut, apiDelete } from '@/lib/api';
import type { ConsumptionByDayResponse, TransactionListParams, TransactionListResult, PixQrCodeResponse } from '@troia-v3/types';
import type {
  CompanyCardResponse,
  AddCardRequest,
  CreditDashboardResponse,
  CreditInvoice,
  InvoiceDetailResponse,
  SubscribePlanRequest,
  PurchaseCreditsRequest,
  UpdateAutoRechargeRequest,
  CreditAlert,
  CreateCreditAlertRequest,
  SubscriptionResult,
  PurchaseResult,
  ChangePlanResult,
  PlanResponse,
  AIModelConsumptionItem,
  ChannelConsumptionItem,
  ChangePaymentMethodRequest,
  ChangePaymentMethodResult,
  CancelSubscriptionRequest,
  CancelSubscriptionResult,
  ReactivateSubscriptionRequest,
  ReactivateSubscriptionResult,
} from '../types';

/**
 * Billing API Service
 * Integração com backend endpoints:
 * - /credits/*
 * - /plans/*
 */
export class BillingService {
  // ============================================================================
  // DASHBOARD
  // ============================================================================

  /**
   * Get credit dashboard (balance, subscription, consumption)
   * GET /credits/dashboard
   */
  static async getDashboard(signal?: AbortSignal): Promise<CreditDashboardResponse> {
    return apiGet<CreditDashboardResponse>('/credits/dashboard', { signal });
  }

  // ============================================================================
  // INVOICES
  // ============================================================================

  /**
   * Get all invoices
   * GET /credits/invoices
   */
  static async getInvoices(signal?: AbortSignal): Promise<CreditInvoice[]> {
    return apiGet<CreditInvoice[]>('/credits/invoices', { signal });
  }

  /**
   * Get invoice detail by cycle number
   * GET /credits/invoices/:cycle
   */
  static async getInvoiceDetail(cycle: number, signal?: AbortSignal): Promise<InvoiceDetailResponse> {
    return apiGet<InvoiceDetailResponse>(`/credits/invoices/${cycle}`, { signal });
  }

  // ============================================================================
  // CARDS
  // ============================================================================

  /**
   * Get company cards
   * GET /credits/cards
   */
  static async getCards(signal?: AbortSignal): Promise<CompanyCardResponse[]> {
    return apiGet<CompanyCardResponse[]>('/credits/cards', { signal });
  }

  /**
   * Add a new card
   * POST /credits/cards
   */
  static async addCard(data: AddCardRequest, signal?: AbortSignal): Promise<CompanyCardResponse> {
    return apiPost<CompanyCardResponse>('/credits/cards', data, { signal, _handleErrorLocally: true });
  }

  /**
   * Delete a card by index
   * DELETE /credits/cards/:index
   */
  static async deleteCard(index: number, signal?: AbortSignal): Promise<void> {
    return apiDelete(`/credits/cards/${index}`, { signal });
  }

  /**
   * Set card as default by index
   * PUT /credits/cards/:index/default
   */
  static async setDefaultCard(index: number, signal?: AbortSignal): Promise<void> {
    return apiPut<void>(`/credits/cards/${index}/default`, {}, { signal });
  }

  // ============================================================================
  // SUBSCRIPTION & PLAN
  // ============================================================================

  /**
   * Subscribe to a plan
   * POST /credits/subscribe
   */
  static async subscribe(data: SubscribePlanRequest, signal?: AbortSignal): Promise<SubscriptionResult> {
    return apiPost<SubscriptionResult>('/credits/subscribe', data, { signal, _handleErrorLocally: true });
  }

  /**
   * Get PIX QR code for pending subscription
   * GET /credits/pix-qrcode
   */
  static async getPixQrCode(signal?: AbortSignal): Promise<PixQrCodeResponse> {
    return apiGet<PixQrCodeResponse>('/credits/pix-qrcode', { signal });
  }

  /**
   * Change plan
   * PUT /credits/plan
   */
  static async changePlan(planId: string, signal?: AbortSignal): Promise<ChangePlanResult> {
    return apiPut<ChangePlanResult>('/credits/plan', { planId }, { signal });
  }

  /**
   * Change payment method of existing subscription
   * PUT /credits/payment-method
   */
  static async changePaymentMethod(data: ChangePaymentMethodRequest, signal?: AbortSignal): Promise<ChangePaymentMethodResult> {
    return apiPut<ChangePaymentMethodResult>('/credits/payment-method', data, { signal });
  }

  /**
   * Cancel subscription
   * POST /credits/cancel-subscription
   */
  static async cancelSubscription(data: CancelSubscriptionRequest, signal?: AbortSignal): Promise<CancelSubscriptionResult> {
    return apiPost<CancelSubscriptionResult>('/credits/cancel-subscription', data, { signal, _handleErrorLocally: true });
  }

  /**
   * Reactivate a cancelled/suspended subscription
   * POST /credits/reactivate-subscription
   */
  static async reactivateSubscription(data: ReactivateSubscriptionRequest, signal?: AbortSignal): Promise<ReactivateSubscriptionResult> {
    return apiPost<ReactivateSubscriptionResult>('/credits/reactivate-subscription', data, { signal, _handleErrorLocally: true });
  }

  /**
   * Purchase additional credits
   * POST /credits/purchase
   */
  static async purchaseCredits(data: PurchaseCreditsRequest, signal?: AbortSignal): Promise<PurchaseResult> {
    return apiPost<PurchaseResult>('/credits/purchase', data, { signal });
  }

  /**
   * Update auto-recharge settings
   * PUT /credits/auto-recharge
   */
  static async updateAutoRecharge(data: UpdateAutoRechargeRequest, signal?: AbortSignal): Promise<void> {
    return apiPut<void>('/credits/auto-recharge', data, { signal });
  }

  // ============================================================================
  // ALERTS
  // ============================================================================

  /**
   * Get credit alerts
   * GET /credits/alerts
   */
  static async getAlerts(signal?: AbortSignal): Promise<CreditAlert[]> {
    return apiGet<CreditAlert[]>('/credits/alerts', { signal });
  }

  /**
   * Create credit alert
   * POST /credits/alerts
   */
  static async createAlert(data: CreateCreditAlertRequest, signal?: AbortSignal): Promise<void> {
    return apiPost<void>('/credits/alerts', data, { signal });
  }

  /**
   * Update credit alert by index
   * PUT /credits/alerts/:index
   */
  static async updateAlert(index: number, data: Partial<CreateCreditAlertRequest>, signal?: AbortSignal): Promise<void> {
    return apiPut<void>(`/credits/alerts/${index}`, data, { signal });
  }

  /**
   * Delete credit alert by index
   * DELETE /credits/alerts/:index
   */
  static async deleteAlert(index: number, signal?: AbortSignal): Promise<void> {
    return apiDelete(`/credits/alerts/${index}`, { signal });
  }

  // ============================================================================
  // PLANS
  // ============================================================================

  /**
   * Get available plans
   * GET /plans
   */
  static async getPlans(signal?: AbortSignal): Promise<PlanResponse[]> {
    const response = await apiGet<{ items: PlanResponse[] }>('/plans', { signal });
    return response.items;
  }

  /**
   * Get plan by ID
   * GET /plans/:id
   */
  static async getPlan(id: string, signal?: AbortSignal): Promise<PlanResponse> {
    return apiGet<PlanResponse>(`/plans/${id}`, { signal });
  }

  // ============================================================================
  // CONSUMPTION & TRANSACTIONS
  // ============================================================================

  /**
   * Get daily consumption breakdown
   * GET /credits/consumption
   */
  static async getConsumptionByDay(signal?: AbortSignal): Promise<ConsumptionByDayResponse> {
    return apiGet<ConsumptionByDayResponse>('/credits/consumption', { signal });
  }

  /**
   * Get AI consumption by model
   * GET /credits/consumption/ai-models
   */
  static async getConsumptionByAIModel(signal?: AbortSignal): Promise<AIModelConsumptionItem[]> {
    return apiGet<AIModelConsumptionItem[]>('/credits/consumption/ai-models', { signal });
  }

  /**
   * Get consumption by channel
   * GET /credits/consumption/channels
   */
  static async getConsumptionByChannel(signal?: AbortSignal): Promise<ChannelConsumptionItem[]> {
    return apiGet<ChannelConsumptionItem[]>('/credits/consumption/channels', { signal });
  }

  /**
   * Get paginated transactions with filters
   * GET /credits/transactions
   */
  static async getTransactions(filters: TransactionListParams, signal?: AbortSignal): Promise<TransactionListResult> {
    const params = new URLSearchParams();
    if (filters.category) params.set('category', filters.category);
    if (filters.type) params.set('type', filters.type);
    if (filters.source) params.set('source', filters.source);
    if (filters.startDate) params.set('startDate', filters.startDate);
    if (filters.endDate) params.set('endDate', filters.endDate);
    if (filters.page) params.set('page', String(filters.page));
    if (filters.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return apiGet<TransactionListResult>(`/credits/transactions${qs ? `?${qs}` : ''}`, { signal });
  }
}
